/**
 * Dynamix compression: the four codecs used inside leaf chunks
 * (docs/formats/compression.md, VERIFIED). Red Baron's volumes use type 2
 * (LZW, see lzw.ts) for essentially everything and the expansion patch
 * archive uses type 3 (LH1, see lh1.ts) exclusively; types 0 and 1 are
 * implemented for completeness since the engine accepts them even though
 * no shipped Red Baron chunk uses type 1.
 *
 * A compressed leaf begins with a 5-byte header, counted inside the
 * chunk's declared size:
 *
 * ```
 * offset 0x00  uint8    compressionType   0 stored, 1 RLE, 2 LZW, 3 LH1
 * offset 0x01  uint32le uncompressedSize
 * offset 0x05  byte[]   compressed payload
 * ```
 *
 * Not every leaf chunk has this header — which leaf chunks do is a fact
 * about the *format* (SCR/BMP/PAL/FNT/...), not about compression, so that
 * dispatch lives in the format-specific decoders. This module only turns
 * `(compressionType, payload, uncompressedSize)` into decompressed bytes,
 * plus a `decodeCompressedChunk` helper for the common case of a leaf that
 * *does* start with the 5-byte header.
 */

import { BinaryReader } from "./binary-reader.ts";
import { decodeLh1 } from "./lh1.ts";
import { decodeLzw } from "./lzw.ts";

export enum CompressionType {
  Stored = 0,
  Rle = 1,
  Lzw = 2,
  Lh1 = 3,
}

/** Decompresses `payload` (compressed bytes only, header already stripped) to exactly `uncompressedSize` bytes. */
export function decompress(type: CompressionType, payload: Uint8Array, uncompressedSize: number): Uint8Array {
  switch (type) {
    case CompressionType.Stored:
      return decodeStored(payload, uncompressedSize);
    case CompressionType.Rle:
      return decodeRle(payload, uncompressedSize);
    case CompressionType.Lzw:
      return decodeLzw(payload, uncompressedSize);
    case CompressionType.Lh1:
      return decodeLh1(payload, uncompressedSize);
    default:
      throw new Error(`compression: unknown compression type ${type as number}`);
  }
}

/** A leaf chunk's payload, split into its 5-byte compression header and the compressed bytes that follow. */
export interface CompressedChunkHeader {
  readonly type: CompressionType;
  readonly uncompressedSize: number;
  readonly payload: Uint8Array;
}

/** Reads the 5-byte compression header at the start of a leaf chunk's raw bytes. */
export function readCompressionHeader(leafData: Uint8Array): CompressedChunkHeader {
  const reader = new BinaryReader(leafData);
  const type = reader.u8();
  const uncompressedSize = reader.u32();
  const payload = reader.bytes(reader.remaining());
  return { type: type as CompressionType, uncompressedSize, payload };
}

/** Reads the 5-byte compression header and decompresses in one step — the common case for a packed leaf chunk. */
export function decodeCompressedChunk(leafData: Uint8Array): Uint8Array {
  const { type, uncompressedSize, payload } = readCompressionHeader(leafData);
  return decompress(type, payload, uncompressedSize);
}

function decodeStored(payload: Uint8Array, uncompressedSize: number): Uint8Array {
  if (payload.length !== uncompressedSize) {
    throw new Error(`compression(stored): payload is ${payload.length} bytes, expected ${uncompressedSize}`);
  }
  return payload;
}

/**
 * Type 1 — RLE, high-bit-repeat (REPORTED; not observed in shipped Red
 * Baron data, implemented for completeness per docs/formats/compression.md).
 */
function decodeRle(payload: Uint8Array, uncompressedSize: number): Uint8Array {
  const out = new Uint8Array(uncompressedSize);
  let outPos = 0;
  let inPos = 0;
  while (outPos < uncompressedSize) {
    if (inPos >= payload.length) {
      throw new Error(`compression(RLE): ran out of input at output byte ${outPos}/${uncompressedSize}`);
    }
    const code = payload[inPos++]!;
    if (code === 0x80) {
      // 0x80 encodes a zero-length repeat: a no-op.
      continue;
    }
    if (code < 0x80) {
      const count = code;
      if (inPos + count > payload.length) {
        throw new Error(`compression(RLE): literal run of ${count} bytes overruns input at output byte ${outPos}`);
      }
      out.set(payload.subarray(inPos, inPos + count), outPos);
      inPos += count;
      outPos += count;
    } else {
      const count = code & 0x7f;
      if (inPos >= payload.length) {
        throw new Error(`compression(RLE): missing repeat value at output byte ${outPos}`);
      }
      const value = payload[inPos++]!;
      out.fill(value, outPos, outPos + count);
      outPos += count;
    }
  }
  return out;
}
